import React, { useContext } from "react";
import styled, { css } from "styled-components";
import Popover from "common/Popover/Popover";
import TextWithIcon from "common/Text/TextWithIcon";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { AlertCircle, AlertTriangle } from "lucide-react";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces, PrecisionType } from "@shared/types";
import { useSelector } from "react-redux";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { DISPLAY_CURRENCIES } from "@shared/common/constants";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import { getLocaleStringWithPrecision } from "@shared/utils/numbers";
import { hexToRGB } from "@shared/utils/misc";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import {
  StrategyOrder,
  SummaryData,
} from "@shared/features/bots/BotForm/common/types";
import useOrderSummary from "@shared/features/bots/BotForm/OrdersSummary/useOrderSummary";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens";

// The grid bot's levels in the new look: three facts up top (what it
// invests, what it buys at the start, the range), then the levels as a
// ladder from the top of the range down. Levels the bot buys at the start
// (above the current price) and the buy orders waiting below are two groups
// split by the current price, instead of a check in every row.

const NewLookGridsSummary = ({
  orders = [],
  isFetching,
  errorMessage,
  showAmount,
  showPrice,
  firstOrderDistanceText,
  orderType,
}: SummaryData) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { symbolPriceData } = useContext(BotFormContext)!;
  const displayCurrency = useSelector(selectDisplayCurrency);
  const { getPrecision } = usePrecisions();
  const isBtc = displayCurrency === DISPLAY_CURRENCIES.BTC;
  const amountPrecision =
    getPrecision(isBtc ? PrecisionType.BTC_AMOUNT : PrecisionType.USD_AMOUNT) ||
    0;
  const pricePrecision = symbolPriceData?.precision?.price;
  const currentPrice = symbolPriceData?.data;

  const amountOf = (order: StrategyOrder) =>
    (isBtc ? order.btcAmount : order.usdAmount) || 0;
  const formatAmount = (value: number) =>
    getLocaleStringWithPrecision(value, amountPrecision);
  const formatPrice = (value: number) =>
    getLocaleStringWithPrecision(value, pricePrecision);
  const levelsText = (count: number) =>
    count === 1
      ? t("ordersSummary.grid.levels_one", { count })
      : t("ordersSummary.grid.levels_other", { count });

  // The first level is the top of the range: no order there.
  const levels = orders.slice(1);
  const immediate = levels.filter((order) => order.isImmediate);
  const total = levels.reduce((sum, order) => sum + amountOf(order), 0);
  const startTotal = immediate.reduce((sum, order) => sum + amountOf(order), 0);
  const prices = orders.map((order) => order.price).filter((p) => p > 0);
  // Group only when the levels bought at the start are the top run.
  const firstWaiting = levels.findIndex((order) => !order.isImmediate);
  const isGrouped =
    immediate.length > 0 &&
    (firstWaiting === -1 ||
      levels.slice(firstWaiting).every((order) => !order.isImmediate));

  const rowProps = { showAmount, showPrice, firstOrderDistanceText, orderType };
  const renderRow = (order: StrategyOrder, index: number) => (
    <GridRow
      key={index}
      order={order}
      index={index}
      showTag={!isGrouped}
      formatPrice={formatPrice}
      {...rowProps}
    />
  );

  return (
    <LoadingSpinnerOverlay isLoading={isFetching}>
      {errorMessage && (
        <Errors role="alert">
          {Array.isArray(errorMessage)
            ? errorMessage.map((error) => <div key={error}>{error}</div>)
            : errorMessage}
        </Errors>
      )}
      {levels.length > 0 && (
        <Facts>
          <Fact>
            <FactLabel>{t("ordersSummary.grid.total")}</FactLabel>
            <FactValue>
              <TextWithIcon text={formatAmount(total)} />
            </FactValue>
            <FactMeta>
              {levelsText(levels.length)}
            </FactMeta>
          </Fact>
          <Fact>
            <FactLabel>{t("ordersSummary.grid.bought_at_start")}</FactLabel>
            <FactValue>
              <TextWithIcon text={formatAmount(startTotal)} />
            </FactValue>
            <FactMeta>
              {levelsText(immediate.length)}
            </FactMeta>
          </Fact>
          {prices.length > 1 && (
            <Fact>
              <FactLabel>{t("ordersSummary.grid.range")}</FactLabel>
              <FactValue $wrap={true}>
                {formatPrice(Math.min(...prices))} –{" "}
                {formatPrice(Math.max(...prices))}
              </FactValue>
              {currentPrice ? (
                <FactMeta>
                  {t("ordersSummary.grid.price_now", {
                    price: formatPrice(currentPrice),
                  })}
                </FactMeta>
              ) : null}
            </Fact>
          )}
        </Facts>
      )}
      <Head>
        <span>{t("ordersSummary.grid.price")}</span>
        <span>{t("ordersSummary.grid.amount")}</span>
      </Head>
      <List>
        {orders[0] && renderRow(orders[0], 0)}
        {isGrouped ? (
          <>
            <Group>
              {t("ordersSummary.grid.group_start", {
                count: immediate.length,
              })}
            </Group>
            {immediate.map((order, i) => renderRow(order, i + 1))}
            {currentPrice ? (
              <Now>
                <span>
                  {t("ordersSummary.grid.current_price", {
                    price: formatPrice(currentPrice),
                  })}
                </span>
              </Now>
            ) : null}
            {levels.length > immediate.length && (
              <Group>
                {t("ordersSummary.grid.group_waiting", {
                  count: levels.length - immediate.length,
                })}
              </Group>
            )}
            {levels
              .slice(immediate.length)
              .map((order, i) => renderRow(order, i + 1 + immediate.length))}
          </>
        ) : (
          levels.map((order, i) => renderRow(order, i + 1))
        )}
      </List>
    </LoadingSpinnerOverlay>
  );
};

export default NewLookGridsSummary;

const GridRow = ({
  order,
  index,
  showTag,
  formatPrice,
  showAmount,
  showPrice,
  firstOrderDistanceText,
  orderType,
}: {
  order: StrategyOrder;
  index: number;
  showTag: boolean;
  formatPrice: (value: number) => string;
} & Pick<
  SummaryData,
  "showAmount" | "showPrice" | "firstOrderDistanceText" | "orderType"
>) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { priceData, amountData, amountTextType, errorData, showWarningIcon } =
    useOrderSummary({
      order,
      index,
      showAmount,
      showPrice,
      firstOrderDistanceText,
      orderType,
    });
  const isTop = index === 0;
  // "(6.98%)" -> "+6.98%"; leave text such as "Pending" as it is.
  const distance = (priceData.distance || "").replace(/^\((.*)\)$/, "$1");
  const signedDistance = /^\d/.test(distance) ? `+${distance}` : distance;

  return (
    <Row $top={isTop}>
      <Index>{index + 1}</Index>
      <Price>
        {priceData.price !== undefined && (
          <b>{formatPrice(priceData.price)}</b>
        )}
        <Distance>{signedDistance}</Distance>
        {showTag && order.isImmediate && !isTop && (
          <Tag>{t("ordersSummary.grid.at_start")}</Tag>
        )}
      </Price>
      <Amount $type={amountTextType}>
        {isTop ? (
          <Muted>{t("ordersSummary.grid.no_order")}</Muted>
        ) : (
          <>
            {amountData.amount ? (
              <TextWithIcon text={amountData.amount} />
            ) : (
              amountData.text
            )}
            {amountData.ratio && <Muted>{amountData.ratio}</Muted>}
          </>
        )}
        {errorData && (
          <Popover
            content={`${errorData.title} ${errorData.error}`}
            placement="topRight"
          >
            <AlertCircle size={14} />
          </Popover>
        )}
        {!errorData && showWarningIcon && <AlertTriangle size={14} />}
      </Amount>
    </Row>
  );
};

const Errors = styled.div`
  margin-bottom: 16px;
  padding: 10px 12px;
  border-radius: ${NEW_LOOK_RADIUS.control}px;
  background: ${(p) => hexToRGB(p.theme.colors.red, 0.08)};
  color: ${(p) => p.theme.colors.red};
  font-size: 13px;
  line-height: 19px;
`;
const Facts = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px;
  margin-bottom: 20px;
`;
const Fact = styled.div`
  min-width: 0;
  padding: 12px 14px;
  border-radius: ${NEW_LOOK_RADIUS.control + 2}px;
  background: ${(p) => getNewLookTokens(p.theme).well};
`;
const FactLabel = styled.div`
  ${newLookType.label};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const FactValue = styled.div<{ $wrap?: boolean }>`
  margin-top: 4px;
  font-size: 16px;
  line-height: 22px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  ${(p) =>
    p.$wrap
      ? css`
          text-wrap: balance;
        `
      : css`
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        `}
`;
const FactMeta = styled.div`
  margin-top: 2px;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const Head = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0 4px 8px 36px;
  border-bottom: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  ${newLookType.micro};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const List = styled.div`
  display: flex;
  flex-direction: column;
`;
const Row = styled.div<{ $top?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 40px;
  padding: 0 4px;
  border-bottom: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  ${(p) =>
    p.$top &&
    css`
      color: ${getNewLookTokens(p.theme).muted};
    `}
`;
const Index = styled.span`
  flex: none;
  width: 20px;
  text-align: right;
  font-size: 11px;
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const Price = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: baseline;
  gap: 8px;
  b {
    font-weight: 500;
    color: ${(p) => getNewLookTokens(p.theme).ink};
  }
`;
const Distance = styled.span`
  font-size: 12px;
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const Tag = styled.span`
  align-self: center;
  padding: 1px 6px;
  border-radius: ${NEW_LOOK_RADIUS.chip}px;
  background: ${(p) => p.theme.colors.lightBlue};
  color: ${(p) => p.theme.colors.primaryBlue};
  font-size: 11px;
  line-height: 16px;
  font-weight: 500;
`;
const Amount = styled.div<{ $type?: string }>`
  flex: none;
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-weight: 500;
  color: ${(p) =>
    p.$type === "danger"
      ? p.theme.colors.red
      : p.$type === "warning"
      ? p.theme.colors.orange
      : getNewLookTokens(p.theme).ink};
  svg {
    align-self: center;
  }
`;
const Muted = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const Group = styled.div`
  padding: 14px 4px 6px 36px;
  ${newLookType.micro};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
// The current price: a blue rule with its value, between the two groups.
const Now = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  height: 28px;
  margin-top: 4px;
  &::before {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    top: 50%;
    border-top: 1px dashed ${(p) => p.theme.colors.primaryBlue};
  }
  span {
    position: relative;
    margin-left: 32px;
    padding: 2px 8px;
    border-radius: ${NEW_LOOK_RADIUS.chip}px;
    background: ${(p) => p.theme.colors.primaryBlue};
    color: #fff;
    font-size: 11px;
    line-height: 16px;
    font-weight: 500;
    font-variant-numeric: tabular-nums;
  }
`;
